// Phase 3: Reports V2 - Focus-Aware Report Builder
// Complete report creation interface with AI narratives and templates

import React, { useState, useMemo, useEffect } from 'react';
import { FileText, Download, Save, Sparkles, X, Copy, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import PageHeader from '../components/PageHeader';
import { FocusSelector } from '../components/reports/FocusSelector';
import { EntitySelector } from '../components/reports/EntitySelector';
import { DateRangePicker } from '../components/reports/DateRangePicker';
import { DataSourceToggles } from '../components/reports/DataSourceToggles';
import { DataFreshnessAlert } from '../components/reports/DataFreshnessAlert';
import { 
  useReportContext, 
  useTemplates, 
  useGenerateNarrative, 
  useSaveReport,
  useApplyTemplate 
} from '../hooks/useReportContext';
import { useReleases } from '../hooks/useReleases';
import { useContacts } from '../hooks/useContacts';
import { useCampaigns } from '../hooks/useCampaigns';
import type { ReportFocus, DateRange, ReportSources } from '../../shared/types/reports';

export const ReportsV2: React.FC = () => {
  // State
  const [focus, setFocus] = useState<ReportFocus>('release');
  const [selectedEntityId, setSelectedEntityId] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<DateRange>({
    start: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0],
    preset: 'last_7_days',
  });
  const [dataSources, setDataSources] = useState<ReportSources>({
    meta: true,
    spotify: true,
    hypeddit: false,
  });
  const [selectedCampaignIds, setSelectedCampaignIds] = useState<string[]>([]);
  const [aiNarrative, setAiNarrative] = useState<string>('');
  const [generatingNarrative, setGeneratingNarrative] = useState(false);
  const [comparisonMode, setComparisonMode] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);

  // Fetch entity options based on focus
  const { releases, loading: releasesLoading } = useReleases({});
  const { contacts, loading: contactsLoading } = useContacts({});
  const { campaigns: allCampaigns, loading: campaignsLoading } = useCampaigns({});
  
  // API action hooks
  const generateNarrative = useGenerateNarrative();
  const saveReport = useSaveReport();
  const applyTemplate = useApplyTemplate();

  // Get entity options based on focus
  const entityOptions = useMemo(() => {
    switch (focus) {
      case 'release':
        return (releases || []).map(r => ({
          id: r.id,
          name: r.title,
          subtitle: r.artist?.name || 'Unknown Artist',
          metadata: { release_date: r.release_date, artwork_url: r.artwork_url },
        }));
      case 'client':
        return (contacts || []).map(c => ({
          id: c.id,
          name: c.name,
          subtitle: c.company || c.email,
        }));
      case 'artist':
        return [];
      case 'campaign':
        return (allCampaigns || []).map(c => ({
          id: c.id,
          name: c.name,
          subtitle: c.status,
        }));
      default:
        return [];
    }
  }, [focus, releases, contacts, allCampaigns]);

  const selectedEntity = entityOptions.find(opt => opt.id === selectedEntityId) || null;

  // Fetch report context when entity is selected
  const { 
    data: reportContext, 
    isLoading: contextLoading,
    error: contextError 
  } = useReportContext({
    focus,
    entityId: selectedEntityId,
    startDate: dateRange.start,
    endDate: dateRange.end,
    enabled: !!selectedEntityId,
  });

  // Fetch templates
  const { data: templates = [], isLoading: templatesLoading } = useTemplates();

  // Auto-select campaigns when report context loads
  useEffect(() => {
    if (reportContext?.relatedEntities?.campaigns) {
      const campaignIds = reportContext.relatedEntities.campaigns.map(c => c.id);
      setSelectedCampaignIds(campaignIds);
    }
  }, [reportContext]);

  // Get selected campaigns details
  const selectedCampaigns = useMemo(() => {
    return reportContext?.relatedEntities?.campaigns?.filter(c => 
      selectedCampaignIds.includes(c.id)
    ) || [];
  }, [reportContext, selectedCampaignIds]);

  const canGenerateReport = 
    selectedEntityId && 
    reportContext?.dataFreshness?.meta?.status !== 'missing' &&
    reportContext?.dataFreshness?.spotify?.status !== 'missing';

  // Handle AI narrative generation
  const handleGenerateNarrative = async () => {
    if (!reportContext?.metrics) return;

    try {
      setGeneratingNarrative(true);
      const response = await generateNarrative({
        metrics: {
          streams: reportContext.metrics.spotify?.streams || 0,
          listeners: reportContext.metrics.spotify?.listeners || 0,
          streamsChangePct: 0, // TODO: Calculate from previous period
          listenersChangePct: 0,
          playlistAdds: reportContext.metrics.spotify?.playlistAdds || 0,
          totalSpend: reportContext.metrics.meta?.spend || 0,
          impressions: reportContext.metrics.meta?.impressions || 0,
          ctr: reportContext.metrics.meta?.ctr || 0,
          cpm: reportContext.metrics.meta?.cpm || 0,
        },
        template: 'release_weekly',
        focus,
        entityName: reportContext.entity.name,
        artistName: reportContext.entity.metadata.artist_name,
      });

      setAiNarrative(response.narrative.html);
      toast.success('AI narrative generated successfully');
    } catch (error: any) {
      toast.error(`Failed to generate narrative: ${error.message}`);
    } finally {
      setGeneratingNarrative(false);
    }
  };

  // Handle template save
  const handleSaveTemplate = async () => {
    if (!templateName || !selectedEntityId) return;

    try {
      await saveReport({
        type: 'template',
        focus,
        entityId: selectedEntityId,
        name: templateName,
        config: {
          dateRange,
          dataSources,
          selectedCampaigns: selectedCampaignIds,
        },
      });

      toast.success(`Template "${templateName}" saved successfully`);
      setShowSaveTemplate(false);
      setTemplateName('');
    } catch (error: any) {
      toast.error(`Failed to save template: ${error.message}`);
    }
  };

  // Handle HTML export
  const handleExportHTML = () => {
    if (!canGenerateReport) return;

    // Generate HTML report
    const htmlContent = generateReportHTML();
    
    // Copy to clipboard
    navigator.clipboard.writeText(htmlContent);
    toast.success('Report HTML copied to clipboard! Ready to paste into email.');
  };

  // Generate HTML report
  const generateReportHTML = (): string => {
    if (!reportContext) return '';

    return `
<html>
<head>
<meta charset="UTF-8">
<style>
  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif; line-height: 1.6; color: #333; }
  h2 { color: #1a202c; margin-top: 20px; margin-bottom: 10px; }
  table { border-collapse: collapse; width: 100%; margin: 20px 0; }
  th, td { padding: 12px; text-align: left; border-bottom: 1px solid #e2e8f0; }
  th { background-color: #f7fafc; font-weight: 600; }
  .metric { font-size: 24px; font-weight: bold; color: #2563eb; }
</style>
</head>
<body>
<h1>Week Ending ${new Date(dateRange.end).toLocaleDateString()}</h1>
<h2>${reportContext.entity.name}${reportContext.entity.metadata.artist_name ? ` - ${reportContext.entity.metadata.artist_name}` : ''}</h2>

${reportContext.metrics.spotify ? `
<h3>🎵 Spotify Performance</h3>
<ul>
  <li>Streams: <span class="metric">${reportContext.metrics.spotify.streams.toLocaleString()}</span></li>
  <li>Listeners: <span class="metric">${reportContext.metrics.spotify.listeners.toLocaleString()}</span></li>
  <li>Playlist Adds: ${reportContext.metrics.spotify.playlistAdds}</li>
  <li>Saves: ${reportContext.metrics.spotify.saves}</li>
</ul>
` : ''}

${reportContext.metrics.meta ? `
<h3>📊 Campaign Performance (Rollup)</h3>
<table>
  <tr><th>Metric</th><th>Value</th></tr>
  <tr><td>Total Spend</td><td>$${reportContext.metrics.meta.spend.toFixed(2)}</td></tr>
  <tr><td>Impressions</td><td>${reportContext.metrics.meta.impressions.toLocaleString()}</td></tr>
  <tr><td>Clicks</td><td>${reportContext.metrics.meta.clicks.toLocaleString()}</td></tr>
  <tr><td>CTR</td><td>${reportContext.metrics.meta.ctr.toFixed(2)}%</td></tr>
  <tr><td>CPM</td><td>$${reportContext.metrics.meta.cpm.toFixed(2)}</td></tr>
</table>
` : ''}

${aiNarrative ? `
<h3>📝 Summary</h3>
${aiNarrative}
` : ''}

<hr style="margin: 30px 0; border: 0; border-top: 1px solid #e2e8f0;">
<p style="font-size: 12px; color: #718096;">
  Report generated: ${new Date().toLocaleString()}<br>
  Data as of: Meta (${reportContext.dataFreshness.meta.timestamp ? new Date(reportContext.dataFreshness.meta.timestamp).toLocaleString() : 'N/A'}), 
  Spotify (${reportContext.dataFreshness.spotify.timestamp ? new Date(reportContext.dataFreshness.spotify.timestamp).toLocaleString() : 'N/A'})
</p>
</body>
</html>
    `.trim();
  };

  // Handle campaign removal
  const handleRemoveCampaign = (campaignId: string) => {
    setSelectedCampaignIds(prev => prev.filter(id => id !== campaignId));
  };

  return (
    <div className="p-6 bg-gray-50 dark:bg-gray-900 min-h-full">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <PageHeader
          icon={<FileText className="h-8 w-8 text-blue-600 dark:text-blue-400" />}
          title="Reports V2"
          subtitle="Focus-aware report generation with AI narratives"
        />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column: Configuration */}
          <div className="lg:col-span-1 space-y-6">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 space-y-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Report Configuration
              </h2>

              {/* Focus Selector */}
              <FocusSelector
                value={focus}
                onChange={(newFocus) => {
                  setFocus(newFocus);
                  setSelectedEntityId(null); // Reset selection when focus changes
                }}
              />

              {/* Entity Selector */}
              <EntitySelector
                focus={focus}
                options={entityOptions}
                selected={selectedEntity}
                onChange={(entity) => setSelectedEntityId(entity?.id || null)}
                loading={releasesLoading || contactsLoading}
              />

              {/* Date Range Picker */}
              <DateRangePicker
                value={dateRange}
                onChange={setDateRange}
              />

              {/* Data Source Toggles */}
              <DataSourceToggles
                value={dataSources}
                onChange={setDataSources}
              />
            </div>

            {/* Templates */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
                Templates
              </h3>
              {templatesLoading ? (
                <div className="text-sm text-gray-500 dark:text-gray-400">Loading templates...</div>
              ) : templates.length === 0 ? (
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  No templates saved yet
                </div>
              ) : (
                <div className="space-y-2">
                  {templates.slice(0, 5).map((template: any) => (
                    <button
                      key={template.id}
                      className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {template.name}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {template.focus} • Last used: {template.lastUsedAt ? new Date(template.lastUsedAt).toLocaleDateString() : 'Never'}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right Column: Preview */}
          <div className="lg:col-span-2 space-y-6">
            {/* Data Freshness Alert */}
            {reportContext?.dataFreshness && (
              <DataFreshnessAlert
                meta={reportContext.dataFreshness.meta}
                spotify={reportContext.dataFreshness.spotify}
                hypeddit={reportContext.dataFreshness.hypeddit}
              />
            )}

            {/* Report Preview */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Report Preview
                </h2>
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowSaveTemplate(true)}
                    disabled={!canGenerateReport}
                    className="btn btn-secondary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Save className="h-4 w-4" />
                    Save Template
                  </button>
                  <button
                    onClick={handleExportHTML}
                    disabled={!canGenerateReport}
                    className="btn btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Download className="h-4 w-4" />
                    Export HTML
                  </button>
                </div>
              </div>

              {/* Loading State */}
              {contextLoading && selectedEntityId && (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              )}

              {/* Error State */}
              {contextError && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                  <p className="text-sm text-red-700 dark:text-red-300">
                    Error loading report context: {contextError.message}
                  </p>
                </div>
              )}

              {/* Empty State */}
              {!selectedEntityId && !contextLoading && (
                <div className="text-center py-12">
                  <FileText className="h-16 w-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                    Select a {focus} to begin
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Choose a {focus} from the selector above to load report data
                  </p>
                </div>
              )}

              {/* Report Context Data */}
              {reportContext && selectedEntityId && !contextLoading && (
                <div className="space-y-6">
                  {/* Entity Header */}
                  <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
                    <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                      {reportContext.entity.name}
                    </h3>
                    <div className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                      {reportContext.entity.metadata.artist_name && (
                        <span>{reportContext.entity.metadata.artist_name} • </span>
                      )}
                      {reportContext.entity.metadata.client_name && (
                        <span>{reportContext.entity.metadata.client_name}</span>
                      )}
                    </div>
                  </div>

                  {/* Selected Campaigns (Chips with Remove) */}
                  {selectedCampaigns.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                        Selected Campaigns ({selectedCampaigns.length})
                      </h4>
                      <div className="space-y-2">
                        {selectedCampaigns.map((campaign) => (
                          <div
                            key={campaign.id}
                            className="flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg"
                          >
                            <div className="flex-1">
                              <div className="font-medium text-gray-900 dark:text-gray-100">
                                {campaign.name}
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                Status: {campaign.status} {campaign.external_id && `• Meta ID: ${campaign.external_id}`}
                              </div>
                            </div>
                            <button
                              onClick={() => handleRemoveCampaign(campaign.id)}
                              className="p-1.5 hover:bg-blue-100 dark:hover:bg-blue-900/40 rounded transition-colors"
                              aria-label="Remove campaign"
                            >
                              <X className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Metrics Preview */}
                  {(reportContext.metrics.meta || reportContext.metrics.spotify) && (
                    <div className="space-y-4">
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Metrics Preview
                      </h4>

                      {/* Meta Ads Metrics */}
                      {reportContext.metrics.meta && (
                        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                          <h5 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-2">
                            Meta Ads Campaign Performance
                          </h5>
                          <div className="grid grid-cols-2 gap-3 text-sm">
                            <div>
                              <div className="text-gray-600 dark:text-gray-400">Total Spend</div>
                              <div className="font-semibold text-gray-900 dark:text-gray-100">
                                ${reportContext.metrics.meta.spend.toFixed(2)}
                              </div>
                            </div>
                            <div>
                              <div className="text-gray-600 dark:text-gray-400">CTR</div>
                              <div className="font-semibold text-gray-900 dark:text-gray-100">
                                {reportContext.metrics.meta.ctr.toFixed(2)}%
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Spotify Metrics */}
                      {reportContext.metrics.spotify && (
                        <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
                          <h5 className="text-sm font-semibold text-green-900 dark:text-green-100 mb-2">
                            Spotify Performance
                          </h5>
                          <div className="grid grid-cols-2 gap-3 text-sm">
                            <div>
                              <div className="text-gray-600 dark:text-gray-400">Streams</div>
                              <div className="font-semibold text-gray-900 dark:text-gray-100">
                                {reportContext.metrics.spotify.streams.toLocaleString()}
                              </div>
                            </div>
                            <div>
                              <div className="text-gray-600 dark:text-gray-400">Listeners</div>
                              <div className="font-semibold text-gray-900 dark:text-gray-100">
                                {reportContext.metrics.spotify.listeners.toLocaleString()}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* AI Narrative Section */}
                  <div className="bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 rounded-lg p-6">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                        AI-Generated Summary
                      </h4>
                      {aiNarrative && (
                        <button
                          onClick={() => setAiNarrative('')}
                          className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                        >
                          Clear
                        </button>
                      )}
                    </div>
                    
                    {aiNarrative ? (
                      <div 
                        className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed prose prose-sm max-w-none"
                        dangerouslySetInnerHTML={{ __html: aiNarrative }}
                      />
                    ) : (
                      <div className="text-sm text-gray-500 dark:text-gray-400 italic">
                        AI narrative will appear here once you generate it...
                      </div>
                    )}
                    
                    <button 
                      onClick={handleGenerateNarrative}
                      disabled={!canGenerateReport || generatingNarrative}
                      className="mt-4 btn btn-secondary text-sm flex items-center gap-2 disabled:opacity-50"
                    >
                      <Sparkles className={`h-4 w-4 ${generatingNarrative ? 'animate-spin' : ''}`} />
                      {generatingNarrative ? 'Generating...' : 'Generate AI Summary'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Save Template Modal */}
        {showSaveTemplate && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                Save as Template
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Template Name
                  </label>
                  <input
                    type="text"
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                    placeholder={`${selectedEntity?.name || focus.charAt(0).toUpperCase() + focus.slice(1)} - Weekly Performance`}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                      bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100
                      focus:outline-none focus:ring-2 focus:ring-blue-500"
                    autoFocus
                  />
                </div>

                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-sm">
                  <p className="font-medium text-gray-900 dark:text-gray-100 mb-2">
                    This template will save:
                  </p>
                  <ul className="space-y-1 text-gray-600 dark:text-gray-400">
                    <li>✅ Focus: {focus.charAt(0).toUpperCase() + focus.slice(1)}</li>
                    <li>✅ {focus.charAt(0).toUpperCase() + focus.slice(1)}: {selectedEntity?.name}</li>
                    <li>✅ Date Range: {dateRange.preset?.replace(/_/g, ' ') || 'Custom'} (auto-updates)</li>
                    <li>✅ Data Sources: {Object.entries(dataSources).filter(([_, v]) => v).map(([k]) => k.charAt(0).toUpperCase() + k.slice(1)).join(', ')}</li>
                  </ul>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowSaveTemplate(false);
                    setTemplateName('');
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                    hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveTemplate}
                  disabled={!templateName.trim()}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700
                    disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Save Template
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
